import React from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button, BUTTON_TYPES, Popup, TYPOGRAPHY_WEIGHT } from '_atoms';
import { Typography } from '_atoms/Typography';
import { ReactComponent as Email } from 'dist/icons/socialNetworks/Email.svg';
import { ReactComponent as Phone } from 'dist/icons/socialNetworks/Phone.svg';
import type { DuplicateCandidate } from 'store/reducers/candidateReducer';
import { getDuplicatedData } from './utils';
import './style.scss';

type CandidateDuplicateEntry = DuplicateCandidate & { phone: string[]; email: string[] };

interface PopupCandidateDuplicateProps {
  open: boolean;
  onClose: () => void;
  duplicate?: CandidateDuplicateEntry | null;
  candidateDuplicates: CandidateDuplicateEntry[];
  loading?: boolean;
  isMergeAble?: boolean;
}

export const PopupCandidateDuplicate: React.FC<PopupCandidateDuplicateProps> = ({
  open,
  onClose,
  duplicate,
  candidateDuplicates,
  loading,
  isMergeAble,
}) => {
  const { t } = useTranslation();

  return (
    <Popup
      className="candidate-duplicate-popup"
      footer={null}
      open={open}
      title={t('translation:popup_candidate_duplicate.title')}
      onCancel={onClose}
      loading={loading}
    >
      {candidateDuplicates.map((v, idx) => (
        <React.Fragment key={v.uuid}>
          {!!idx && <Divider />}
          <div key={v.uuid} className="duplicate-item">
            <div className="duplicate-item__header">
              <div className="duplicate-item__header__full-name">
                <Typography.Text
                  weight={TYPOGRAPHY_WEIGHT.BOLD}
                  className={cn({ duplicate: getDuplicatedData(v.full_name_latin, 'full_name_latin', duplicate) })}
                >
                  {v.full_name_latin}
                </Typography.Text>
                <Typography.Caption
                  className={cn({ duplicate: getDuplicatedData(v.full_name_local, 'full_name_local', duplicate) })}
                >
                  {v.full_name_local}
                </Typography.Caption>
              </div>
              {!isMergeAble && (
                <Link
                  className="info-wrapper-link"
                  to={`/candidates/${v.uuid}/show`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button>{t('translation:popup_candidate_duplicate.button_go_to_candidate')}</Button>
                </Link>
              )}
            </div>
            <ul className="duplicate-item__content">
              {v.phone.map(p => (
                <a key={p} href={`tel:${p}`} target="_blank" rel="noopener noreferrer">
                  <li className={cn({ duplicate: getDuplicatedData(p, 'phone', duplicate) })}>
                    <Phone />
                    {p}
                  </li>
                </a>
              ))}
              {v.email.map(e => (
                <a key={e} href={`mailto:${e}`} target="_blank" rel="noopener noreferrer">
                  <li className={cn({ duplicate: getDuplicatedData(e, 'email', duplicate) })}>
                    <Email />
                    {e}
                  </li>
                </a>
              ))}
            </ul>
            {isMergeAble && (
              <div className="duplicate-item__buttons">
                <Link
                  className="info-wrapper-link"
                  to={`/self-submitted-candidates/merge-profile/${v.uuid}/${duplicate?.uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                >
                  <Button>{t('translation:popup_candidate_duplicate.button_merge_profile')}</Button>
                </Link>
                <Link
                  className="info-wrapper-link"
                  to={`/candidates/${v.uuid}/show`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button type={BUTTON_TYPES.PRIMARY}>
                    {t('translation:popup_candidate_duplicate.button_go_to_candidate')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </Popup>
  );
};
